import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';

const MatchingQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { socket, connected } = useSocket();

  // Get pre-filled data from Dashboard navigation state
  const prefilledData = location.state || {};

  // Form states - Load from localStorage or prefilled data
  const [category, setCategory] = useState(() => {
    if (prefilledData.category) return prefilledData.category;
    const saved = localStorage.getItem('vibeyfocus_matching_category');
    return saved || '';
  });
  const [description, setDescription] = useState(() => {
    if (prefilledData.description) return prefilledData.description;
    const saved = localStorage.getItem('vibeyfocus_matching_description');
    return saved || '';
  });
  const [acceptableCategories, setAcceptableCategories] = useState(() => {
    const saved = localStorage.getItem('vibeyfocus_matching_acceptableCategories');
    return saved ? JSON.parse(saved) : [];
  });
  const [sessionLength, setSessionLength] = useState(() => {
    if (prefilledData.sessionLength) return prefilledData.sessionLength;
    const saved = localStorage.getItem('vibeyfocus_matching_sessionLength');
    return saved ? parseInt(saved) : 25;
  });
  const [requestedStartTime, setRequestedStartTime] = useState('');
  const [sessionStyle, setSessionStyle] = useState(() => {
    if (prefilledData.sessionStyle) return prefilledData.sessionStyle;
    const saved = localStorage.getItem('vibeyfocus_matching_sessionStyle');
    return saved || 'focus-only';
  });

  // Status states
  const [isSearching, setIsSearching] = useState(false);
  const [matchingRequestId, setMatchingRequestId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Available categories (match backend validation format)
  const categories = [
    { display: 'General Productivity', value: 'general_productivity' },
    { display: 'Creative', value: 'creative' },
    { display: 'Spiritual', value: 'spiritual' }
  ];

  // Session length options (in minutes)
  const sessionLengthOptions = [25, 50, 75];

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('vibeyfocus_matching_category', category);
  }, [category]);

  useEffect(() => {
    localStorage.setItem('vibeyfocus_matching_description', description);
  }, [description]);

  useEffect(() => {
    localStorage.setItem('vibeyfocus_matching_acceptableCategories', JSON.stringify(acceptableCategories));
  }, [acceptableCategories]);

  useEffect(() => {
    localStorage.setItem('vibeyfocus_matching_sessionLength', sessionLength.toString());
  }, [sessionLength]);

  useEffect(() => {
    localStorage.setItem('vibeyfocus_matching_sessionStyle', sessionStyle);
  }, [sessionStyle]);

  // Initialize requested start time to "now"
  useEffect(() => {
    const now = new Date();
    const roundedMinutes = Math.ceil(now.getMinutes() / 15) * 15;
    now.setMinutes(roundedMinutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    setRequestedStartTime(now.toISOString().slice(0, 16));
  }, []);

  // Socket listeners for matching events
  useEffect(() => {
    if (!socket) return;

    const handleMatchFound = (data) => {
      console.log('Match found:', data);
      setIsSearching(false);
      // Navigate to session room with partner data
      navigate(`/session/${data.sessionId}`, {
        state: {
          partnerInfo: {
            id: data.partnerId,
            name: data.partnerName,
            photo: data.partnerPhoto,
            location: data.partnerLocation,
            category: data.partnerCategory,
            description: data.partnerDescription,
            sessionPreferences: data.partnerSessionPreferences,
          }
        }
      });
    };

    const handleMatchExpired = (data) => {
      console.log('Match expired:', data);
      setIsSearching(false);
      setError(data.message || 'No match found. Please try again.');
    };

    const handleMatchingError = (data) => {
      console.error('Matching error:', data);
      setIsSearching(false);
      setError(data.error || 'An error occurred while matching.');
    };

    socket.on('match-found', handleMatchFound);
    socket.on('match-expired', handleMatchExpired);
    socket.on('matching-error', handleMatchingError);

    return () => {
      socket.off('match-found', handleMatchFound);
      socket.off('match-expired', handleMatchExpired);
      socket.off('matching-error', handleMatchingError);
    };
  }, [socket, navigate]);

  const handleCategoryToggle = (cat) => {
    setAcceptableCategories(prev =>
      prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  const handleStartMatching = async (e) => {
    e.preventDefault();
    setError('');

    // Check if user is logged in
    if (!user) {
      setError('Please log in to book a session');
      return;
    }

    // Check if email is verified (skip in development)
    const isDevelopment = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.startsWith('192.168.') ||
                          window.location.hostname.startsWith('10.') ||
                          process.env.NODE_ENV === 'development';

    if (!isDevelopment && !user.verified) {
      setError('Please verify your email before booking a session. Check your inbox for the verification link.');
      return;
    }

    // Validation
    if (!category) {
      setError('Please select your focus category');
      return;
    }

    if (!description.trim()) {
      setError('Please describe what you\'ll be working on');
      return;
    }

    if (!requestedStartTime) {
      setError('Please select a start time');
      return;
    }

    if (!connected) {
      setError('Not connected to server. Please refresh and try again.');
      return;
    }

    setLoading(true);

    try {
      // Map session style to backend fields
      const quietMode = false; // Camera is always on for all 3 styles

      // For radio buttons, user declares their preference
      // But we still set behavior to neutral - they'll show preference in session UI
      const talkDuringSession = false;
      const dontTalkMiddleSession = false;

      // Map session style selection to acceptance fields
      // User only accepts partners with the same preference
      const acceptSocialSessions = sessionStyle === 'social';
      const acceptSilentSessions = sessionStyle === 'silent';
      const acceptFocusOnlySessions = sessionStyle === 'focus-only';

      // Create matching request via API
      const response = await api.post('/matching/request', {
        category,
        description,
        acceptableCategories: acceptableCategories.length > 0 ? acceptableCategories : [category],
        sessionLength,
        requestedStartTime,
        quietMode,
        talkDuringSession,
        dontMatchQuietMode: false,
        dontTalkMiddleSession,
        acceptSocialSessions,
        acceptSilentSessions,
        acceptFocusOnlySessions,
      });

      const { request } = response.data;
      setMatchingRequestId(request.id);
      setIsSearching(true);

      // Join matching queue via Socket.io
      socket.emit('join-matching-queue', {
        requestId: request.id,
        userId: user.id,
        userName: user.first_name,
        userPhoto: user.profile_photo,
        userLocation: user.location,
        category,
        description,
        quietMode,
        talkDuringSession,
      });

      console.log('Joined matching queue with request:', request.id);
    } catch (err) {
      console.error('Error creating matching request:', err);
      setError(err.response?.data?.error || 'Failed to start matching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMatching = async () => {
    console.log('🚫 Cancel button clicked, matchingRequestId:', matchingRequestId);

    if (!matchingRequestId) {
      console.log('❌ No matching request ID found');
      setError('No active matching request to cancel');
      setIsSearching(false);
      return;
    }

    try {
      console.log('📡 Emitting leave-matching-queue socket event');
      // Leave matching queue via Socket.io
      socket.emit('leave-matching-queue', {
        requestId: matchingRequestId,
        userId: user.id,
      });

      console.log('🔄 Calling API to cancel request:', matchingRequestId);
      // Cancel via API
      await api.delete(`/matching/request/${matchingRequestId}`);

      console.log('✅ Successfully cancelled matching request');
      setIsSearching(false);
      setMatchingRequestId(null);
      setError('');
    } catch (err) {
      console.error('❌ Error cancelling matching:', err);
      setError('Failed to cancel. Please try again.');
    }
  };

  if (isSearching) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Finding Your Match</h2>
          <p className="text-gray-600 mb-6">
            We're searching for someone with similar focus goals...
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Category:</span>
              <span className="text-sm font-medium text-gray-900">
                {categories.find(c => c.value === category)?.display || category}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Session Length:</span>
              <span className="text-sm font-medium text-gray-900">{sessionLength} min</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Start Time:</span>
              <span className="text-sm font-medium text-gray-900">
                {new Date(requestedStartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          <button
            onClick={handleCancelMatching}
            className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-md font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start a Focus Session</h1>
          <p className="text-gray-600 mb-8">
            Tell us what you'll be working on and we'll match you with a focus partner
          </p>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleStartMatching}>
            {/* Category Selection */}
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-900 mb-3 tracking-tight">
                What will you focus on? <span className="text-red-500 font-medium">*</span>
              </label>
              <div className="flex flex-wrap gap-3">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                      category === cat.value
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {cat.display}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe your focus goal <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Finishing chapter 3 of my thesis, Coding a new feature..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                rows="3"
                required
              />
            </div>

            {/* Acceptable Categories */}
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-900 mb-3 tracking-tight">
                Also match me with (optional)
              </label>
              <div className="flex flex-wrap gap-3">
                {categories.filter(cat => cat.value !== category).map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleCategoryToggle(cat.value)}
                    className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                      acceptableCategories.includes(cat.value)
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {cat.display}
                  </button>
                ))}
              </div>
            </div>

            {/* Session Length */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Length
              </label>
              <div className="flex flex-wrap gap-2">
                {sessionLengthOptions.map(length => (
                  <button
                    key={length}
                    type="button"
                    onClick={() => setSessionLength(length)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      sessionLength === length
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {length} min
                  </button>
                ))}
              </div>
            </div>

            {/* Start Time */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Start Time
              </label>
              <input
                type="datetime-local"
                value={requestedStartTime}
                onChange={(e) => setRequestedStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            {/* Session Preferences */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Session Style
              </label>
              <div className="space-y-3">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    checked={sessionStyle === 'focus-only'}
                    onChange={() => setSessionStyle('focus-only')}
                    className="w-4 h-4 text-primary-600 border-gray-300 mt-0.5 focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Focus Only</span>
                    <p className="text-xs text-gray-500">Talk before and after the session, stay focused during</p>
                  </div>
                </label>

                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    checked={sessionStyle === 'social'}
                    onChange={() => setSessionStyle('social')}
                    className="w-4 h-4 text-primary-600 border-gray-300 mt-0.5 focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Social Session</span>
                    <p className="text-xs text-gray-500">Open to chatting and taking breaks together during the session</p>
                  </div>
                </label>

                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    checked={sessionStyle === 'silent'}
                    onChange={() => setSessionStyle('silent')}
                    className="w-4 h-4 text-primary-600 border-gray-300 mt-0.5 focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Silent Focus</span>
                    <p className="text-xs text-gray-500">Camera on for accountability, no talking at all</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-md font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !connected}
                className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Starting...' : connected ? 'Find a Match' : 'Connecting...'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MatchingQueue;
